import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  ClearCanvassersResponse,
  RequestCanvassersPayload,
  RequestCanvassersResponse,
  UpdateJobDatesPayload,
  UpdateSchedulePayload,
  UpdateScheduleResponse,
} from './components/canvassers/types';
import {
  AssignAgentsToJobParams,
  AssignAgentsToJobResponse,
  CreateJobParams,
  CreateJobResponse,
  CreateScheduleParams,
  CreateScheduleResponse,
  JobDetailsResponse,
} from './types';

const _createSchedule = async (
  schedule: CreateScheduleParams
): Promise<AxiosResponse<CreateScheduleResponse>> => {
  return api.post('schedule', schedule);
};
const useCreateSchedule = () => {
  return useMutation({
    mutationFn: _createSchedule,
  });
};

const _createJob = async (
  schedule: CreateJobParams
): Promise<AxiosResponse<CreateJobResponse>> => {
  return api.post('v2/p2p', schedule);
};
const useCreateJob = () => {
  return useMutation({
    mutationFn: _createJob,
  });
};

const _assignAgentsToJob = async (
  assignAgentsData: AssignAgentsToJobParams
): Promise<AxiosResponse<AssignAgentsToJobResponse>> => {
  const { jobId, assignedAgents: rawAssignedAgents } = assignAgentsData;
  const agent_ids = rawAssignedAgents.map((a) => ({
    id: a.agent_id,
    is_responder: a.isResponder,
  }));
  return api.post(`1to1/jobs/${jobId}/agents`, { agent_ids });
};

const useAssignAgentsToJob = () => {
  return useMutation({
    mutationFn: _assignAgentsToJob,
  });
};

// Update Job API
const _updateJob = async (params: {
  jobId: string;
  jobData: CreateJobParams;
}): Promise<AxiosResponse<JobDetailsResponse>> => {
  const { jobId, jobData } = params;
  return api.patch(`v2/p2p/${jobId}`, jobData);
};

const useUpdateJob = () => {
  return useMutation({
    mutationFn: _updateJob,
  });
};

// Update Schedule API (placeholder for future implementation)
const _updateSchedule = async (params: {
  scheduleId: number;
  scheduleData: CreateScheduleParams;
}): Promise<AxiosResponse<CreateScheduleResponse>> => {
  const { scheduleId, scheduleData } = params;
  // TODO: Implement when schedule editing is added
  return api.patch(`schedule/${scheduleId}`, scheduleData);
};

const useUpdateSchedule = () => {
  return useMutation({
    mutationFn: _updateSchedule,
  });
};

// Canvasser API Functions

// Update Schedule for Canvassers
const _updateScheduleForCanvassers = async (params: {
  scheduleId: number;
  scheduleData: UpdateSchedulePayload;
}): Promise<AxiosResponse<UpdateScheduleResponse>> => {
  const { scheduleId, scheduleData } = params;
  return api.patch(`v2/schedule/${scheduleId}`, scheduleData);
};

const useUpdateScheduleForCanvassers = () => {
  return useMutation({
    mutationFn: _updateScheduleForCanvassers,
  });
};

// Update Job Dates
const _updateJobDates = async (params: {
  jobId: string;
  jobData: UpdateJobDatesPayload;
}): Promise<AxiosResponse<JobDetailsResponse>> => {
  const { jobId, jobData } = params;
  return api.patch(`v2/p2p/${jobId}`, jobData);
};

const useUpdateJobDates = () => {
  return useMutation({
    mutationFn: _updateJobDates,
  });
};

// Request Canvassers
const _requestCanvassers = async (params: {
  jobId: string;
  canvasserData: RequestCanvassersPayload;
}): Promise<AxiosResponse<RequestCanvassersResponse>> => {
  const { jobId, canvasserData } = params;
  return api.post(`v2/p2p/${jobId}/request_canvassers`, canvasserData);
};

const useRequestCanvassers = () => {
  return useMutation({
    mutationFn: _requestCanvassers,
  });
};

// Clear Canvassers
const _clearCanvassers = async (params: {
  jobId: string;
}): Promise<AxiosResponse<ClearCanvassersResponse>> => {
  const { jobId } = params;
  return api.post(`v2/p2p/${jobId}/clear_canvassers`, {});
};

const useClearCanvassers = () => {
  return useMutation({
    mutationFn: _clearCanvassers,
  });
};

// Assign Canvasser to Job
const _assignCanvasserToJob = async (params: {
  jobId: string;
  canvasserId: string;
  isResponder?: boolean;
}): Promise<AxiosResponse<JobDetailsResponse>> => {
  const { jobId, canvasserId, isResponder = true } = params;
  return api.post(`1to1/jobs/${jobId}/assignagent`, {
    agent: {
      id: canvasserId,
      is_responder: isResponder,
    },
  });
};

const useAssignCanvasserToJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: _assignCanvasserToJob,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['fetch-canvasser-details', { canvasserId: variables.canvasserId }],
      });
    },
  });
};

// Unassign Canvasser from Job
// Area code overlays for a US/Canadian state (used to populate the
// "Job Area Codes" multi-select on job creation/edit).
export type AreaCodeOverlay = {
  areacodes: string[];
  description: string;
};

const _fetchAreaCodeOverlays = async (
  state: string
): Promise<AxiosResponse<AreaCodeOverlay[]>> => {
  return api.get('v2/p2p/overlays', { params: { state } });
};

const useFetchAreaCodeOverlays = (state?: string) => {
  const isNational = !state || state === 'USA' || state === 'CAN';
  return useQuery({
    queryKey: ['fetch-area-code-overlays', { state }],
    queryFn: () => _fetchAreaCodeOverlays(state!),
    enabled: !!state && !isNational,
    staleTime: 60 * 60 * 1000,
    retry: 0,
  });
};

const _unassignCanvasserFromJob = async (params: {
  jobId: string;
  canvasserId: string;
}): Promise<AxiosResponse<JobDetailsResponse>> => {
  const { jobId, canvasserId } = params;
  return api.post(`1to1/jobs/${jobId}/unassignagent`, {
    agent_id: canvasserId,
  });
};

const useUnassignCanvasserFromJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: _unassignCanvasserFromJob,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['fetch-canvasser-details', { canvasserId: variables.canvasserId }],
      });
    },
  });
};

export interface CloneJobPayload {
  name?: string;
  agent_assignments?: boolean;
  did_state?: string;
  did_npa_subset?: string[];
  identity_id?: string;
}

export interface CloneJobResponse {
  id: string;
  name: string;
  account_id?: string;
}

const _cloneJob = async (params: {
  jobId: string;
  payload: CloneJobPayload;
}): Promise<AxiosResponse<CloneJobResponse>> => {
  const { jobId, payload } = params;
  return api.post(`v2/p2p/${jobId}/clone`, payload);
};

const useCloneJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: _cloneJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

const _deleteJob = async (jobId: string): Promise<AxiosResponse<void>> => {
  return api.delete(`v2/p2p/${jobId}`);
};

const useDeleteJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: _deleteJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

export {
  useAssignAgentsToJob,
  useAssignCanvasserToJob,
  useClearCanvassers,
  useCloneJob,
  useCreateJob,
  useCreateSchedule,
  useDeleteJob,
  useFetchAreaCodeOverlays,
  useRequestCanvassers,
  useUnassignCanvasserFromJob,
  useUpdateJob,
  useUpdateJobDates,
  useUpdateSchedule,
  // Canvasser hooks
  useUpdateScheduleForCanvassers,
};
